import React, { useState, useEffect } from "react";
import { Warranty } from "../../types/Warranty";
import { getAxios } from "../../utils/endpoints";
import { SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";

export const useEditWarrantyForm = (warrantyId: number) => {
  const navigate = useNavigate();

  const [warranty, setWarranty] = React.useState<Warranty>({
    isAdmin: 1,
    city: "",
    dateOfTransfer: "",
    dealerId: 0,
    dealerName: "",
    firstName: "",
    id: warrantyId,
    lastName: "",
    phone: "",
    print: "",
    state: "",
    street: "",
    streetNumber: "",
    zip: "",
    newHomeOwnerName: "",
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [snackbarState, setSnackbarState] = useState({
    isOpen: false,
    message: "",
    level: "success",
  });

  const [isViewChangesDialogOpen, setIsViewChangesDialogOpen] =
    useState<boolean>(false);
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [newHomeOwnerName, setNewHomeOwnerName] = useState<string>("");
  const [dealers, setDealers] = useState<string[]>([]);

  const onFetchWarranty = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getAxios(`/warranties/${warrantyId}`, "GET");

      const data: Warranty = await response.data;

      setWarranty(data);
      setAdminNotes(data.adminNotes!);
    } catch (error) {
      setSnackbarState({
        message: "Could not get warranty",
        level: "error",
        isOpen: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeViewChangesDialog = () => {
    setIsViewChangesDialogOpen(false);
  };

  const openViewChangesDialog = () => {
    setIsViewChangesDialogOpen(true);
  };

  const onHandleCloseSnackbar = () => {
    setSnackbarState({ ...snackbarState, isOpen: false });
  };

  const onFetchDealers = async (): Promise<void> => {
    const response = await getAxios("/profiles", "GET", undefined, {
      ProfileMode: false,
      AdminMode: false,
    });

    const items = await response.data.items.map((val: any) => val.username);

    setDealers(items);
  };

  const onChangeSelect = async (
    event: SelectChangeEvent<unknown>
  ): Promise<void> => {
    setWarranty((prevState) => ({
      ...prevState,
      dealerId: event.target.value as number,
    }));
  };

  const onCancel = () => {
    navigate("/view-warranties");
  };

  const onUpdate = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getAxios(
        "/warranties",
        "PUT",
        {
          Id: warrantyId,
          FirstName: warranty.firstName,
          LastName: warranty.lastName,
          StreetNumber: warranty.streetNumber,
          Street: warranty.street,
          City: warranty.city,
          State: warranty.state,
          Zip: warranty.zip,
          Phone: warranty.phone,
          NewHomeOwnerName: warranty.newHomeOwnerName,
          DateOfTransfer: warranty.dateOfTransfer,
          AdminNotes: adminNotes,
          NewDealer: warranty.dealerId,
        },
        undefined
      );

      setSnackbarState({
        message: "Warranty has been updated",
        level: "success",
        isOpen: true,
      });
    } catch (error) {
      setSnackbarState({
        message: "Could not update warranty",
        level: "error",
        isOpen: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setWarranty((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    onFetchWarranty();
    onFetchDealers();
  }, []);

  return {
    warranty,
    adminNotes,
    dealers,
    newHomeOwnerName,
    setAdminNotes,
    setNewHomeOwnerName,
    onChange,
    onChangeSelect,
    onUpdate,
    onCancel,
    snackbarState,
    onHandleCloseSnackbar,
    onFetchWarranty,
    isLoading,
    isViewChangesDialogOpen,
    closeViewChangesDialog,
    openViewChangesDialog,
  };
};
